/**
 * MyEsimsPanel — Lists the user's eSIMs with status, data remaining and
 * actions (install, top-up). Designed to be embedded in Settings or Profile.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Wifi, RefreshCw, QrCode, Plus, Copy, CheckCircle2, AlertCircle, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const STATUS_KEY = {
  active: 'statusActive',
  expired: 'statusExpired',
  cancelled: 'statusCancelled',
  paid: 'statusPending',
  pending_payment: 'statusPaymentPending',
  airalo_failed: 'statusFailed',
};

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const map = {
    active: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
    expired: 'bg-white/10 text-white/60 border-white/20',
    cancelled: 'bg-red-500/20 text-red-300 border-red-400/30',
    paid: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
    pending_payment: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
    airalo_failed: 'bg-red-500/20 text-red-300 border-red-400/30',
  };
  const cls = map[status] || 'bg-white/10 text-white/70 border-white/20';
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${cls}`}>
      {t(`esim.${STATUS_KEY[status] || 'statusPending'}`)}
    </span>
  );
};

const InstallSheet = ({ order, onClose }) => {
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [copying, setCopying] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/api/esim/${order.id}/install?lang=${language || 'en'}`);
        setData(res.data);
      } catch {
        setData({ error: true });
      }
    })();
  }, [order.id, language]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopying(key);
      toast.success(t('esim.copied'));
      setTimeout(() => setCopying(null), 1500);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="esim-install-sheet">
      <div className="w-full sm:max-w-md bg-gradient-to-b from-zinc-900 to-black border-t sm:border border-white/10 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold text-lg">{t('esim.installTitle')}</h3>
            <button onClick={onClose} className="text-white/60 hover:text-white px-2" data-testid="esim-install-close">✕</button>
          </div>
          {!data ? (
            <div className="text-white/60 text-sm">{t('esim.loading')}</div>
          ) : data.error ? (
            <div className="text-red-300 text-sm">{t('common.error') || 'Error'}</div>
          ) : (
            <>
              {data.qr_url && (
                <div className="bg-white p-3 rounded-2xl flex items-center justify-center mb-4">
                  <img src={data.qr_url} alt="QR" className="w-48 h-48 object-contain" />
                </div>
              )}
              <p className="text-white/70 text-xs mb-4">{t('esim.installScan')}</p>

              {data.direct_apple_url && (
                <a
                  href={data.direct_apple_url}
                  className="block w-full bg-white text-black font-bold text-sm py-3 rounded-xl text-center mb-3"
                  data-testid="esim-install-ios"
                >
                  {t('esim.directInstallIos')}
                </a>
              )}

              <div className="space-y-2 mt-2">
                <div className="text-xs uppercase tracking-wider text-white/40 font-bold mt-3">
                  {t('esim.installManualTitle')}
                </div>
                {data.smdp_address && (
                  <CopyRow label={t('esim.smdpAddress')} value={data.smdp_address} onCopy={copy} copied={copying === 'smdp'} cKey="smdp" t={t} />
                )}
                {data.lpa_string && (
                  <CopyRow label="LPA" value={data.lpa_string} onCopy={copy} copied={copying === 'lpa'} cKey="lpa" t={t} />
                )}
                {data.manual_code && (
                  <CopyRow label={t('esim.confirmationCode')} value={data.manual_code} onCopy={copy} copied={copying === 'code'} cKey="code" t={t} />
                )}
                {data.matching_id && (
                  <CopyRow label={t('esim.activationCode')} value={data.matching_id} onCopy={copy} copied={copying === 'match'} cKey="match" t={t} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CopyRow = ({ label, value, onCopy, copied, cKey, t }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-2">
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</div>
      <div className="text-xs text-white font-mono truncate mt-0.5">{value}</div>
    </div>
    <button
      type="button"
      onClick={() => onCopy(value, cKey)}
      className="text-white/70 hover:text-white p-1.5 rounded-lg bg-white/5 hover:bg-white/10"
    >
      {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
    </button>
  </div>
);

const UsageBar = ({ order }) => {
  const { t } = useLanguage();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/esim/${order.id}/usage`);
      setUsage(res.data?.usage || null);
    } catch {
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => {
    if (order.status === 'active') refresh();
  }, [order.status, refresh]);

  if (!usage) {
    return (
      <button
        type="button"
        onClick={refresh}
        disabled={loading}
        className="text-xs text-white/60 hover:text-white flex items-center gap-1.5"
      >
        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        {loading ? t('esim.refreshing') : t('esim.refresh')}
      </button>
    );
  }

  const totalMb = usage.total_mb ?? usage.total ?? 0;
  const remainMb = usage.remaining_mb ?? usage.remaining ?? 0;
  const usedMb = Math.max(0, totalMb - remainMb);
  const pct = totalMb > 0 ? Math.min(100, Math.round((usedMb / totalMb) * 100)) : 0;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-white/60">{t('esim.dataRemaining')}: <span className="text-white font-semibold">{(remainMb / 1024).toFixed(2)} GB</span></span>
        <button type="button" onClick={refresh} disabled={loading} className="text-white/60 hover:text-white">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400"
          style={{ width: `${100 - pct}%` }}
        />
      </div>
    </div>
  );
};

const MyEsimsPanel = ({ embedded = false }) => {
  const { t } = useLanguage();
  const [esims, setEsims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/esim/my-esims');
      setEsims(res.data?.esims || []);
    } catch {
      setEsims([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className={embedded ? '' : 'min-h-screen bg-black text-white pb-24'}>
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wifi className="w-5 h-5 text-purple-400" />
            <h2 className="text-white font-bold text-base" data-testid="my-esims-title">{t('esim.mySectionTitle')}</h2>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-xs text-white/60 hover:text-white flex items-center gap-1.5"
            data-testid="my-esims-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {t('esim.refresh')}
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-24 rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : esims.length === 0 ? (
          <div className="text-center py-12">
            <Wifi className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm" data-testid="my-esims-empty">{t('esim.noEsims')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-esims-list">
            {esims.map((o) => {
              const snap = o.package_snapshot || {};
              return (
                <div
                  key={o.id}
                  className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-4"
                  data-testid={`my-esim-${o.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-white/10">
                      {snap.country_image ? (
                        <img src={snap.country_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Wifi className="w-5 h-5 text-white/40" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-white font-bold text-sm truncate">
                          {snap.country_title || snap.title} · {snap.is_unlimited ? t('esim.unlimited') : snap.data_label}
                        </div>
                        <StatusBadge status={o.status} />
                      </div>
                      <div className="text-[11px] text-white/60 mt-0.5 flex items-center gap-2">
                        <Calendar className="w-3 h-3" />
                        {snap.validity_days} {t('esim.days')}
                        {o.type === 'topup' && <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[9px] font-bold">TOP-UP</span>}
                      </div>
                      {o.status === 'active' && <UsageBar order={o} />}
                    </div>
                  </div>
                  {o.status === 'active' && (
                    <div className="flex gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => setInstalling(o)}
                        className="flex-1 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5"
                        data-testid={`my-esim-install-${o.id}`}
                      >
                        <QrCode className="w-3.5 h-3.5" />
                        {t('esim.installAgain')}
                      </button>
                    </div>
                  )}
                  {o.status === 'airalo_failed' && (
                    <div className="mt-2 text-[11px] text-red-300 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {t('esim.statusFailed')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {installing && <InstallSheet order={installing} onClose={() => setInstalling(null)} />}
    </div>
  );
};

export default MyEsimsPanel;
